/**
 * Queue Repository Implementation (Infrastructure)
 *
 * Implements the domain repository interface.
 * Similar to Flutter's repository implementation pattern.
 *
 * This layer is responsible for:
 * - Coordinating datasources
 * - Error handling and transformation
 * - Caching strategies (if needed)
 * - Data mapping between layers
 */

import { AppException, ErrorCode } from '@/core/exceptions';

import type { QueueRepository } from '../../domain/repositories/queueRepository';
import type {
  CallQueuePaginatedRequest,
  CallQueuePaginatedResponse,
  ReorderRequest,
  MoveQueueResponse,
  CallQueueDeleteRequest,
  DeleteQueueResponse,
  CaseAssignCallQueueRequest,
  MessageResponse,
  CallDetailResponse,
  SubmitCallDetailRequest,
  CallControlAction,
  ContactSearchResult,
  CaseSearchResult,
  ForwardCallRequest,
} from '../../domain/types';
import type { QueueDatasource } from '../datasources';
import { queueDatasource } from '../datasources';

// ============================================================================
// Repository Implementation
// ============================================================================

export class QueueRepositoryImpl implements QueueRepository {
  constructor(private readonly datasource: QueueDatasource = queueDatasource) {}

  async getQueueList(request: CallQueuePaginatedRequest): Promise<CallQueuePaginatedResponse> {
    try {
      return await this.datasource.getQueueList(request);
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch queue list');
    }
  }

  async moveQueue(request: ReorderRequest): Promise<MoveQueueResponse> {
    try {
      return await this.datasource.moveQueue(request);
    } catch (error) {
      throw this.handleError(error, 'Failed to move queue');
    }
  }

  async deleteQueues(requests: CallQueueDeleteRequest[]): Promise<DeleteQueueResponse> {
    try {
      return await this.datasource.deleteQueues(requests);
    } catch (error) {
      throw this.handleError(error, 'Failed to delete queues');
    }
  }

  async bulkAddCasesToQueue(request: CaseAssignCallQueueRequest): Promise<MessageResponse> {
    try {
      return await this.datasource.bulkAddCasesToQueue(request);
    } catch (error) {
      throw this.handleError(error, 'Failed to add cases to call queue');
    }
  }

  async getCallDetail(callId: number): Promise<CallDetailResponse> {
    try {
      return await this.datasource.getCallDetail(callId);
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch call detail');
    }
  }

  async submitCallDetail(callId: number, data: SubmitCallDetailRequest): Promise<void> {
    try {
      await this.datasource.submitCallDetail(callId, data);
    } catch (error) {
      throw this.handleError(error, 'Failed to submit call detail');
    }
  }

  async controlCall(action: CallControlAction): Promise<MessageResponse> {
    try {
      return await this.datasource.controlCall(action);
    } catch (error) {
      throw this.handleError(error, 'Failed to execute call control');
    }
  }

  async makeNextCall(): Promise<MessageResponse> {
    try {
      return await this.datasource.makeNextCall();
    } catch (error) {
      throw this.handleError(error, 'Failed to make next call');
    }
  }

  async searchContacts(callId: number, query: string): Promise<ContactSearchResult[]> {
    try {
      return await this.datasource.searchContacts(callId, query);
    } catch (error) {
      throw this.handleError(error, 'Failed to search contacts');
    }
  }

  async searchCasesForContact(callId: number, contactId: number): Promise<CaseSearchResult[]> {
    try {
      return await this.datasource.searchCasesForContact(callId, contactId);
    } catch (error) {
      throw this.handleError(error, 'Failed to search cases');
    }
  }

  async forwardCall(request: ForwardCallRequest): Promise<MessageResponse> {
    try {
      return await this.datasource.forwardCall(request);
    } catch (error) {
      throw this.handleError(error, 'Failed to forward call');
    }
  }

  async cancelForwardCall(): Promise<MessageResponse> {
    try {
      return await this.datasource.cancelForwardCall();
    } catch (error) {
      throw this.handleError(error, 'Failed to cancel call forward');
    }
  }

  /**
   * Transform errors to domain exceptions
   */
  private handleError(error: unknown, fallbackMessage: string): AppException {
    if (error instanceof AppException) {
      return error;
    }

    if (error instanceof Error) {
      return new AppException({
        code: ErrorCode.UNKNOWN_ERROR,
        message: error.message,
        userMessage: error.message || fallbackMessage,
      });
    }

    return new AppException({
      code: ErrorCode.UNKNOWN_ERROR,
      message: fallbackMessage,
      userMessage: fallbackMessage,
    });
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const queueRepository: QueueRepository = new QueueRepositoryImpl();
